import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  'pending',
  'paid',
  'failed',
  'cancelled',
  'cash_on_delivery',
  'whatsapp_order',
  'completed',
];

function money(currency, value) {
  return `${currency} ${Number(value || 0).toLocaleString()}`;
}

function StatusBadge({ status }) {
  const styles = {
    paid: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-slate-100 text-slate-700',
    cash_on_delivery: 'bg-blue-100 text-blue-800',
    whatsapp_order: 'bg-violet-100 text-violet-800',
    completed: 'bg-emerald-100 text-emerald-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
      {status || '-'}
    </span>
  );
}

export default function StoreOrderDetail({ adminMode = false }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = adminMode ? `/admin/store-orders/${orderId}` : `/dashboard/store-orders/${orderId}`;
      const r = await api.get(endpoint);
      setData(r.data.order || null);
      setStatus(r.data.order?.status || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load order');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orderId, adminMode]);

  const saveStatus = async () => {
    try {
      const endpoint = adminMode ? `/admin/store-orders/${orderId}` : `/dashboard/store-orders/${orderId}`;
      const r = await api.patch(endpoint, { status });
      setData(r.data.order || null);
      toast.success('Order updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!data) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 text-center">
          <p className="text-slate-500">Order not found.</p>
        </CardContent>
      </Card>
    );
  }

  const canEdit = adminMode || ['pending', 'cash_on_delivery', 'whatsapp_order'].includes(data.status);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Store Order</h1>
          <p className="text-slate-500 text-sm mt-0.5">Reference {data.order_number || data.reference_code || data.transaction_reference}</p>
          <div className="mt-2">
            <StatusBadge status={data.status} />
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(adminMode ? '/admin/store-orders' : '/dashboard/store/orders')}>
          Back
        </Button>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Order Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-xs text-slate-500">Customer</p>
            <p className="text-sm font-medium text-slate-900">{data.customer_name || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Phone</p>
            <p className="text-sm font-medium text-slate-900">{data.customer_phone || data.phone || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Email</p>
            <p className="text-sm font-medium text-slate-900">{data.customer_email || data.email || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Delivery Address</p>
            <p className="text-sm font-medium text-slate-900">{data.delivery_address || data.address || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Method</p>
            <p className="text-sm font-medium text-slate-900">{data.order_method || data.payment_method || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Gateway</p>
            <p className="text-sm font-medium text-slate-900">{data.gateway || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Subtotal</p>
            <p className="text-sm font-medium text-slate-900">{money(data.currency, data.subtotal || data.amount || 0)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-sm font-medium text-slate-900">{money(data.currency, data.total || data.amount || 0)}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Items</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.items || []).map((item, index) => (
                <TableRow key={item.id || index}>
                  <TableCell>{item.product_name || '-'}</TableCell>
                  <TableCell>{item.quantity || 1}</TableCell>
                  <TableCell>{money(item.currency || data.currency, item.unit_price || 0)}</TableCell>
                  <TableCell>{money(item.currency || data.currency, item.line_total || 0)}</TableCell>
                </TableRow>
              ))}
              {(data.items || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-slate-500 py-8">No item records found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Status</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="w-full sm:max-w-xs">
            <Select value={status} onValueChange={setStatus} disabled={!canEdit}>
              <SelectTrigger><SelectValue placeholder="Update status" /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={saveStatus} disabled={!canEdit} className="bg-emerald-700 hover:bg-emerald-800 text-white">
            Save status
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
